import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from "@material-ui/core";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import TSUser from "components/Forms/TSUser";
import { VehicleDriverService } from "_services/vehicle-driver.service";
import { VehicleAdminGuard } from "./vehicleAuth";

const errorMessage = (error, fallback) => error.response?.data?.msg || error.response?.data?.message || fallback;

function VehicleDriverManagerContent() {
  const [drivers, setDrivers] = useState([]);
  const [orderMap, setOrderMap] = useState({});
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await VehicleDriverService.getAll();
      if (response.data.result !== 200 || !Array.isArray(response.data.data)) throw new Error("Invalid response");
      const dList = response.data.data;
      setDrivers(dList);
      const initialOrders = {};
      dList.forEach((d, idx) => {
        initialOrders[d.userName] = d.sortOrder && d.sortOrder > 0 ? d.sortOrder : idx + 1;
      });
      setOrderMap(initialOrders);
      setHasOrderChanges(false);
      setLoaded(true);
    } catch (err) {
      if (err.response?.status !== 401) setError(errorMessage(err, "ไม่สามารถโหลดรายชื่อคนขับรถได้ กรุณากดโหลดใหม่"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);

  const handleOrderChange = (userName, val) => {
    const num = val === "" ? "" : Math.max(1, parseInt(val, 10) || 1);
    setOrderMap(prev => ({ ...prev, [userName]: num }));
    setHasOrderChanges(true);
  };

  const handleMoveOrder = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= visibleDrivers.length) return;

    const currentDriver = visibleDrivers[index];
    const targetDriver = visibleDrivers[targetIndex];

    const currentOrder = orderMap[currentDriver.userName] ?? (index + 1);
    const targetOrder = orderMap[targetDriver.userName] ?? (targetIndex + 1);

    setOrderMap(prev => ({
      ...prev,
      [currentDriver.userName]: targetOrder,
      [targetDriver.userName]: currentOrder
    }));
    setHasOrderChanges(true);
  };

  const saveOrders = async () => {
    if (busyRef.current || !hasOrderChanges) return;
    busyRef.current = true;
    setBusy(true); setError(""); setSuccess("");
    try {
      const ordersPayload = Object.entries(orderMap).map(([userName, sortOrder]) => ({
        userName,
        sortOrder: Number(sortOrder) || 0
      }));
      const response = await VehicleDriverService.updateOrders(ordersPayload);
      if (response.data.result !== 200) {
        const failure = new Error("Update orders failed");
        failure.response = response;
        throw failure;
      }
      setSuccess(response.data.msg || "บันทึกการจัดเรียงลำดับคนขับรถเรียบร้อยแล้ว");
      await loadDrivers();
    } catch (err) {
      if (err.response?.status !== 401) setError(errorMessage(err, "ไม่สามารถบันทึกลำดับคนขับรถได้ กรุณาลองใหม่"));
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  const selectUsers = users => {
    const unique = new Map();
    (Array.isArray(users) ? users : []).forEach(user => {
      if (typeof user?.userName === "string" && user.userName.trim()) unique.set(user.userName.trim().toLowerCase(), user);
    });
    setSelectedUsers(Array.from(unique.values()));
  };

  const saveDrivers = async () => {
    if (busyRef.current || !selectedUsers.length) return;
    busyRef.current = true;
    setBusy(true); setError(""); setSuccess("");
    try {
      const response = await VehicleDriverService.add(selectedUsers.map(user => user.userName.trim()));
      if (response.data.result !== 200) {
        const failure = new Error("Save failed");
        failure.response = response;
        throw failure;
      }
      setSuccess(response.data.msg || "บันทึกรายชื่อคนขับรถเรียบร้อยแล้ว");
      setSelectedUsers([]); setPickerOpen(false);
      await loadDrivers();
    } catch (err) {
      if (err.response?.status !== 401) setError(errorMessage(err, "ไม่สามารถบันทึกรายชื่อคนขับรถได้ กรุณาลองใหม่"));
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  const removeDriver = async () => {
    if (busyRef.current || !removeTarget) return;
    busyRef.current = true;
    setBusy(true); setError(""); setSuccess("");
    try {
      const response = await VehicleDriverService.remove(removeTarget.userName);
      if (response.data.result !== 200) {
        const failure = new Error("Remove failed");
        failure.response = response;
        throw failure;
      }
      setRemoveTarget(null);
      setSuccess(response.data.msg || "นำผู้ใช้งานออกจากรายชื่อคนขับรถแล้ว");
      await loadDrivers();
    } catch (err) {
      if (err.response?.status !== 401) setError(errorMessage(err, "ไม่สามารถนำผู้ใช้งานออกได้ กรุณาลองใหม่"));
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  const keyword = search.trim().toLowerCase();
  const visibleDrivers = drivers
    .filter(driver => `${driver.userName} ${driver.fullName || ""}`.toLowerCase().includes(keyword))
    .sort((a, b) => {
      const ordA = Number(orderMap[a.userName]) || (a.sortOrder > 0 ? a.sortOrder : 999999);
      const ordB = Number(orderMap[b.userName]) || (b.sortOrder > 0 ? b.sortOrder : 999999);
      if (ordA !== ordB) return ordA - ordB;
      return (a.fullName || a.userName).localeCompare(b.fullName || b.userName);
    });

  const locked = busy || loading;
  return (
    <Grid>
      <Col xs={12}>
        {error && !removeTarget && <Typography role="alert" color="error" style={{ marginBottom: 16 }}>{error}</Typography>}
        {success && <Typography role="status" style={{ color: "#2e7d32", marginBottom: 16 }}>{success}</Typography>}
        <Card>
          <CardHeader color="primary">
            <Typography variant="h5" component="h1">จัดการคนขับรถ</Typography>
          </CardHeader>
          <CardBody>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <Typography style={{ fontWeight: "bold" }}>รายชื่อคนขับรถ {drivers.length} คน</Typography>
              <Button color="primary" variant="contained" startIcon={<GroupAddIcon />} disabled={locked || !loaded}
                onClick={() => { setError(""); setSuccess(""); setPickerOpen(true); }}>เพิ่มคนขับรถ</Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={locked || !hasOrderChanges}
                onClick={saveOrders}
                style={{
                  backgroundColor: hasOrderChanges ? "#16a34a" : undefined,
                  color: hasOrderChanges ? "#ffffff" : undefined
                }}
              >
                {busy && hasOrderChanges ? "กำลังบันทึก..." : "บันทึกการเรียงลำดับ"}
              </Button>
              <Button startIcon={<RefreshIcon />} disabled={locked} onClick={() => { setError(""); loadDrivers(); }}>โหลดใหม่</Button>
              {loading && <CircularProgress size={24} aria-label="กำลังโหลดรายชื่อคนขับรถ" />}
            </div>
            <TextField label="ค้นหา Username หรือชื่อ" variant="outlined" size="small" value={search}
              onChange={event => setSearch(event.target.value)} style={{ marginBottom: 16, minWidth: 260 }} />
            <TableContainer component={Paper}>
              <Table aria-label="รายชื่อคนขับรถ">
                <TableHead><TableRow>
                  <TableCell style={{ width: 140 }}>ลำดับ</TableCell>
                  <TableCell>Username</TableCell><TableCell>ชื่อ-สกุล</TableCell><TableCell>ตำแหน่ง</TableCell>
                  <TableCell>สถานะบัญชีผู้ใช้</TableCell><TableCell align="right">จัดการ</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {visibleDrivers.map((driver, idx) => <TableRow key={driver.userName}>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="number"
                          min="1"
                          max="999"
                          aria-label={`ลำดับของ ${driver.userName}`}
                          value={orderMap[driver.userName] ?? ""}
                          onChange={e => handleOrderChange(driver.userName, e.target.value)}
                          disabled={locked}
                          style={{
                            width: 52,
                            height: 32,
                            textAlign: "center",
                            borderRadius: 4,
                            border: "1px solid #cbd5e1",
                            fontWeight: "bold",
                            fontSize: "0.9rem"
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ minWidth: 26, height: 28, padding: "0 4px", fontSize: "0.75rem", lineHeight: 1 }}
                          onClick={() => handleMoveOrder(idx, -1)}
                          disabled={locked || idx === 0}
                          title="เลื่อนขึ้น"
                          aria-label={`เลื่อน ${driver.userName} ขึ้น`}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ minWidth: 26, height: 28, padding: "0 4px", fontSize: "0.75rem", lineHeight: 1 }}
                          onClick={() => handleMoveOrder(idx, 1)}
                          disabled={locked || idx === visibleDrivers.length - 1}
                          title="เลื่อนลง"
                          aria-label={`เลื่อน ${driver.userName} ลง`}
                        >
                          ▼
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{driver.userName}</TableCell><TableCell>{driver.fullName || driver.userName}</TableCell>
                    <TableCell>{driver.positionName || "-"}</TableCell>
                    <TableCell><Chip size="small" label={driver.isUserActive ? "ใช้งานได้" : "บัญชีไม่ใช้งาน"}
                      style={{ color: driver.isUserActive ? "#1b5e20" : "#b71c1c" }} /></TableCell>
                    <TableCell align="right"><Button color="secondary" disabled={locked}
                      aria-label={`นำ ${driver.userName} ออกจากรายชื่อคนขับรถ`}
                      onClick={() => { setError(""); setRemoveTarget(driver); }}>นำออก</Button></TableCell>
                  </TableRow>)}
                  {!visibleDrivers.length && <TableRow><TableCell colSpan={6} align="center">
                    {loading ? "กำลังโหลดรายชื่อ..." : !loaded ? "โหลดรายชื่อไม่สำเร็จ" : drivers.length ? "ไม่พบรายชื่อที่ค้นหา" : "ยังไม่มีรายชื่อคนขับรถ"}
                  </TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
        {pickerOpen && <Card>
          <CardHeader color="success"><Typography variant="h6">เพิ่มรายชื่อคนขับรถ</Typography></CardHeader>
          <CardBody>
            <Typography style={{ marginBottom: 16 }}>เลือกเจ้าหน้าที่ได้หลายคน แล้วกดบันทึกเพื่อเพิ่ม Username ลงในรายชื่อคนขับรถ</Typography>
            <fieldset disabled={locked} style={{ border: 0, padding: 0, margin: 0, pointerEvents: locked ? "none" : "auto" }}>
              <TSUser value={selectedUsers} isMultiple={true} onChange={selectUsers} />
            </fieldset>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 20 }}>
              <Typography>เลือก {selectedUsers.length} คน</Typography>
              <Button color="primary" variant="contained" disabled={locked || !selectedUsers.length} onClick={saveDrivers}>
                {busy ? "กำลังบันทึก..." : "บันทึกคนขับรถ"}
              </Button>
              <Button disabled={locked} onClick={() => { setPickerOpen(false); setSelectedUsers([]); setError(""); }}>ยกเลิก</Button>
            </div>
          </CardBody>
        </Card>}
        <Dialog open={!!removeTarget} onClose={() => { if (!busy) { setRemoveTarget(null); setError(""); } }} aria-labelledby="remove-driver-title">
          <DialogTitle id="remove-driver-title">นำออกจากรายชื่อคนขับรถ</DialogTitle>
          <DialogContent>
            <Typography>{removeTarget?.fullName || removeTarget?.userName} ({removeTarget?.userName})</Typography>
            {error && <Typography role="alert" color="error">{error}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => { setRemoveTarget(null); setError(""); }}>ยกเลิก</Button>
            <Button color="secondary" disabled={busy} onClick={removeDriver}>{busy ? "กำลังดำเนินการ..." : "ยืนยันนำออก"}</Button>
          </DialogActions>
        </Dialog>
      </Col>
    </Grid>
  );
}

export default function VehicleDriverManager() {
  return (
    <VehicleAdminGuard>
      <VehicleDriverManagerContent />
    </VehicleAdminGuard>
  );
}
